import { memo, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { VISA_GREENS, VOA_RED, DIASPORA } from "../data/nations";

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const NAME_TO_CODE = {
  Morocco: "MA", Algeria: "DZ", Tunisia: "TN", Libya: "LY", Egypt: "EG",
  Sudan: "SD", "S. Sudan": "SS", Chad: "TD", Niger: "NE", Mali: "ML",
  Mauritania: "MR", Senegal: "SN", "Guinea-Bissau": "GW", Guinea: "GN",
  "Sierra Leone": "SL", Liberia: "LR", "Côte d'Ivoire": "CI", Ghana: "GH",
  Togo: "TG", Benin: "BJ", Nigeria: "NG", Cameroon: "CM",
  "Central African Rep.": "CF", "Eq. Guinea": "GQ", Gabon: "GA", Congo: "CG",
  "Dem. Rep. Congo": "CD", Angola: "AO", Zambia: "ZM", Namibia: "NA",
  Botswana: "BW", "South Africa": "ZA", Lesotho: "LS", eSwatini: "SZ",
  Zimbabwe: "ZW", Mozambique: "MZ", Malawi: "MW", Madagascar: "MG",
  "United Republic of Tanzania": "TZ", Tanzania: "TZ", Kenya: "KE",
  Somalia: "SO", Somaliland: "SO", Ethiopia: "ET", Eritrea: "ER",
  Djibouti: "DJ", Rwanda: "RW", Uganda: "UG", Burundi: "BI",
  "Cape Verde": "CV", "Cabo Verde": "CV", Comoros: "KM", Mauritius: "MU",
  Seychelles: "SC", "São Tomé and Principe": "ST", Gambia: "GM", "The Gambia": "GM",
};

const greenFor = (code) => VISA_GREENS[code.charCodeAt(0) % VISA_GREENS.length];

function AfricaMap({ nations, onSelect }) {
  const [hover, setHover] = useState(null);
  const byCode = useMemo(() => Object.fromEntries(nations.map((n) => [n.code, n])), [nations]);
  const kush = useMemo(() => nations.find((n) => n.founding), [nations]);

  return (
    <div className="relative rounded-2xl border border-[#00A651]/20 bg-[#0A0C0E] overflow-hidden gold-glow" data-testid="africa-map">
      <div className="absolute top-4 left-4 z-10 pointer-events-none">
        <p className="font-official text-[10px] tracking-[0.3em] uppercase text-[#00A651]">The Borderless Continent</p>
        <p className="font-official text-[10px] tracking-[0.15em] uppercase text-[#8A99AD] mt-1.5 h-4" data-testid="map-hover-label">
          {hover ? `${hover.name} — ${hover.visaFree ? "Visa-Free" : "Visa on Arrival"}` : "Hover a nation — tap to open its file"}
        </p>
      </div>
      <ComposableMap projection="geoMercator" projectionConfig={{ center: [24, 4], scale: 400 }} width={800} height={620} style={{ width: "100%", height: "auto" }}>
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const code = NAME_TO_CODE[geo.properties.name];
              const nation = code ? byCode[code] : null;
              const fill = nation ? (nation.visaFree ? greenFor(nation.code) : VOA_RED) : "#14181D";
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={fill}
                  stroke="#0A0C0E"
                  strokeWidth={0.6}
                  onMouseEnter={() => nation && setHover(nation)}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => nation && onSelect(nation)}
                  style={{
                    default: {
                      outline: "none",
                      cursor: nation ? "pointer" : "default",
                      filter: nation && nation.visaFree ? "drop-shadow(0 0 5px rgba(74,222,128,0.45))" : "none",
                      transition: "fill 0.3s",
                    },
                    hover: { outline: "none", fill: nation ? "#00A651" : "#1B2129", cursor: nation ? "pointer" : "default" },
                    pressed: { outline: "none" },
                  }}
                />
              );
            })
          }
        </Geographies>
        <Marker coordinates={[33.737, 21.8245]} onClick={() => kush && onSelect(kush)} style={{ default: { cursor: "pointer" }, hover: { cursor: "pointer" } }} data-testid="kush-map-marker">
          <circle r={10} fill="none" stroke="#00A651" strokeWidth={0.8} opacity={0.5}>
            <animate attributeName="r" values="6;14" dur="2s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.7;0" dur="2s" repeatCount="indefinite" />
          </circle>
          <circle r={4} fill="#00A651" stroke="#0A0C0E" strokeWidth={1.2} />
          <text x={9} y={3} fontSize={9} fill="#00A651" style={{ fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.1em" }}>
            KUSH
          </text>
        </Marker>
        <Marker coordinates={[-25, 12]} onClick={() => onSelect(DIASPORA)} style={{ default: { cursor: "pointer" }, hover: { cursor: "pointer" } }} data-testid="diaspora-map-marker">
          <circle r={10} fill="none" stroke="#4ADE80" strokeWidth={0.8} opacity={0.5}>
            <animate attributeName="r" values="6;14" dur="2.6s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.7;0" dur="2.6s" repeatCount="indefinite" />
          </circle>
          <circle r={4} fill="#4ADE80" stroke="#0A0C0E" strokeWidth={1.2} />
          <text x={9} y={3} fontSize={9} fill="#4ADE80" style={{ fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.1em" }}>
            6TH REGION
          </text>
        </Marker>
      </ComposableMap>
      <div className="flex flex-wrap gap-x-6 gap-y-2 px-5 py-4 border-t border-[#00A651]/12 font-official text-[9px] tracking-[0.2em] uppercase" data-testid="map-legend">
        <span className="flex items-center gap-2 text-[#4ADE80]">
          <span className="w-2.5 h-2.5 rounded-full bg-[#4ADE80] shadow-[0_0_8px_rgba(74,222,128,0.8)]" />
          Visa-Free
        </span>
        <span className="flex items-center gap-2" style={{ color: VOA_RED }}>
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: VOA_RED }} />
          Visa on Arrival
        </span>
        <span className="flex items-center gap-2 text-[#00A651]">
          <span className="w-2.5 h-2.5 rounded-full bg-[#00A651]" />
          Kingdom of Kush — Bir Tawil
        </span>
      </div>
    </div>
  );
}

export default memo(AfricaMap);
